import { useCallback, useMemo, useReducer } from 'react';
import axios from 'axios';
import { DateUtils } from 'react-day-picker';

const initialState = {
  holdGroup: { hold_group_entries: [] },
};

function reducer(state, action) {
  const { type } = action;
  switch (type) {
    case 'DATE_CLICKED': {
      const entries = state.holdGroup.hold_group_entries || [];
      const existing = entries.find((e) => DateUtils.isSameDay(new Date(e.hold.start_time), action.date));

      if (existing) {
        // Toggle _destroy so the user can undo an accidental tap without
        // losing the position they already picked.
        const next = entries.map((e) => (
          DateUtils.isSameDay(new Date(e.hold.start_time), action.date)
            ? { ...e, _destroy: !e._destroy }
            : e
        ));
        return { ...state, holdGroup: { ...state.holdGroup, hold_group_entries: next } };
      }
      const next = [...entries, { hold: { start_time: action.date } }];
      return { ...state, holdGroup: { ...state.holdGroup, hold_group_entries: next } };
    }
    case 'POSITION_UPDATED': {
      const next = state.holdGroup.hold_group_entries.map((e) => {
        if (!DateUtils.isSameDay(new Date(e.hold.start_time), action.date)) return e;
        return { ...e, hold: { ...e.hold, position: parseInt(action.position, 10) } };
      });
      return { ...state, holdGroup: { ...state.holdGroup, hold_group_entries: next } };
    }
    case 'TARGET_TOGGLED': {
      const next = state.holdGroup.hold_group_entries.map((e) => {
        if (!DateUtils.isSameDay(new Date(e.hold.start_time), action.date)) return e;
        return { ...e, hold: { ...e.hold, is_target: !e.hold.is_target } };
      });
      return { ...state, holdGroup: { ...state.holdGroup, hold_group_entries: next } };
    }
    case 'ENTRY_REMOVED': {
      const next = state.holdGroup.hold_group_entries.map((e) => (
        DateUtils.isSameDay(new Date(e.hold.start_time), action.date)
          ? { ...e, _destroy: true }
          : e
      ));
      return { ...state, holdGroup: { ...state.holdGroup, hold_group_entries: next } };
    }
    case 'AVAILABILITY_UPDATED': {
      const next = state.holdGroup.hold_group_entries.map((e) => {
        if (!DateUtils.isSameDay(new Date(e.hold.start_time), action.date)) return e;
        if (e._destroy) return e;
        const defaultTop = action.defaultTopHold || 1;
        const firstAvailable = action.availability
          .slice(defaultTop - 1, -1)
          .find((a) => a.hold_title === null);
        return {
          ...e,
          hold: {
            ...e.hold,
            position: firstAvailable ? firstAvailable.position : defaultTop,
            hold_availability: action.availability,
          },
        };
      });
      return { ...state, holdGroup: { ...state.holdGroup, hold_group_entries: next } };
    }
    case 'SEED':
      return { holdGroup: { hold_group_entries: action.entries || [] } };
    case 'RESET':
      return initialState;
    default:
      return state;
  }
}

export default function useV3HoldDraft({ team }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  const dates = useMemo(() => (
    (state.holdGroup.hold_group_entries || [])
      .filter((e) => !e._destroy)
      .map((e) => new Date(e.hold.start_time))
  ), [state.holdGroup.hold_group_entries]);

  // HoldPositionPicker's prop names map 1:1 onto these dispatchers.
  const holdGroupCalendarDateClicked = useCallback((date) => {
    dispatch({ type: 'DATE_CLICKED', date });
  }, []);

  const holdGroupHoldPositionUpdated = useCallback((startTime, position) => {
    dispatch({ type: 'POSITION_UPDATED', date: new Date(startTime), position });
  }, []);

  const holdPositionTargetToggled = useCallback((startTime) => {
    dispatch({ type: 'TARGET_TOGGLED', date: new Date(startTime) });
  }, []);

  const holdGroupHoldRemoved = useCallback((startTime) => {
    dispatch({ type: 'ENTRY_REMOVED', date: new Date(startTime) });
  }, []);

  const fetchHoldAvailability = useCallback(async (teamArg, roomIds, date) => {
    if (!roomIds || roomIds.length === 0) {
      dispatch({
        type: 'AVAILABILITY_UPDATED',
        date,
        availability: [],
        defaultTopHold: teamArg?.default_top_hold || 1,
      });
      return;
    }
    const dateString = date.toISOString().split('T')[0];
    try {
      const { data } = await axios.get(`/teams/${teamArg.id}/holds/availity`, {
        params: { room_ids: roomIds, date: dateString },
      });
      dispatch({
        type: 'AVAILABILITY_UPDATED',
        date,
        availability: data,
        defaultTopHold: teamArg?.default_top_hold || 1,
      });
    } catch (_) {
      // Swallow — availability is a best-effort enhancement; the user can
      // still pick a position from the static 1–20 list.
    }
  }, []);

  const reset = useCallback(() => dispatch({ type: 'RESET' }), []);

  // Pre-populate the draft with already-held dates (e.g. the offer's linked
  // Hold) so the create-hold modal shows them rather than opening blank.
  // Callers pass browser-local Date start_times (offset for the venue tz).
  const seed = useCallback((holds) => {
    const entries = (holds || []).map((h) => ({
      hold: {
        start_time: h.start_time,
        position: h.position || 1,
        is_target: !!h.is_target,
      },
    }));
    dispatch({ type: 'SEED', entries });
  }, []);

  return {
    holdGroup: state.holdGroup,
    holdGroupDates: dates,
    holdGroupCalendarDateClicked,
    holdGroupHoldPositionUpdated,
    holdPositionTargetToggled,
    holdGroupHoldRemoved,
    fetchHoldAvailability,
    reset,
    seed,
  };
}
